import { memo, useCallback, useEffect, useRef } from 'react'

import { ExpAppEdit } from '@nativewaves/nw-cloud-sdk/exp'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useAppUpdateMutation } from 'hooks/mutations/exp'
import { expAppConfigRoute } from 'pages/NWPlatform/ExpApps/ExpApp'
import { appQueryKeys } from 'services/queryKeys'

const ConfigRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { expAppId: appId } = expAppConfigRoute.useParams()

  const appQuery = useQuery(appQueryKeys.detail({ appId }))
  const { data: expApp } = appQuery

  const { mutate: updateExpApp } = useAppUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: ExpAppEdit) => {
      updateExpApp(
        { appId, expAppEdit: data },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:ExpApp.expAppSaved'), {
              variant: 'success',
            })
          },
          onError: () => {
            enqueueSnackbar(t('domain:ExpApp.errorCreateRetry'), {
              variant: 'error',
            })
          },
        },
      )
    },
    [appId, enqueueSnackbar, t, updateExpApp],
  )

  useEffect(() => {
    if (editorInstanceRef.current && appQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(expApp, null, 4))
    }
  }, [appQuery.isSuccess, expApp])

  if (appQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!expApp) {
    return <NotFound />
  }

  return (
    <Editor
      onSubmit={handleFormSubmit}
      value={expApp}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const Config = memo(ConfigRaw)
